import React, { useState, useEffect, useRef } from "react";
import { Loader } from "./components/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ENDPOINTS } from "./endpoints";
import { useAuth } from "./contexts/AuthContext";
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const placeholderImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

const fotosExecucao = [
  { id: 1, url: placeholderImage },
  { id: 2, url: placeholderImage },
  { id: 3, url: placeholderImage },
];

const fotosFinalizado = [
  { id: 1, url: placeholderImage },
  { id: 2, url: placeholderImage },
  { id: 3, url: placeholderImage },
];

// Definições de cor e ícone para status e prioridade (copiado da grade)
const prioridades = {
  AGUARDANDO: "bg-gray-200 text-gray-900",
  EMERGENCIAL: "bg-red-600 text-white",
  ALTA: "bg-orange-400 text-white",
  MEDIA: "bg-teal-400 text-white",
  BAIXA: "bg-green-700 text-white",
} as const;
const prioridadeIcones = {
  AGUARDANDO: "fa-hourglass-end",
  EMERGENCIAL: "fa-bolt",
  ALTA: "fa-arrow-up",
  MEDIA: "fa-arrow-right",
  BAIXA: "fa-arrow-down",
} as const;
const statusCores = {
  AGUARDANDO: "bg-gray-200 text-gray-900",
  DISTRIBUIDA: "bg-yellow-400 text-gray-900",
  ACEITA: "bg-cyan-400 text-white",
  CONCLUIDA: "bg-green-500 text-white",
  CANCELADA: "bg-red-600 text-white",
} as const;
const statusIcones = {
  AGUARDANDO: "fa-hourglass-end",
  DISTRIBUIDA: "fa-share",
  ACEITA: "fa-check-double",
  CONCLUIDA: "fa-flag-checkered",
  CANCELADA: "fa-times",
} as const;

// Definir uma imagem padrão para quando não houver imagem
const imagemPadrao = "https://cdn-icons-png.flaticon.com/512/2748/2748558.png"; // ícone de imagem genérico

// Toast reutilizável (copiado de UsuariosCRUD)
function Toast({ open, message, type = 'error', onClose }: { open: boolean, message: string, type?: 'error' | 'success', onClose: () => void }) {
  if (!open) return null;
  return (
    <>
      <style>{`
        .toast-anim {
          animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes zoomInFade {
          0% {
            opacity: 0;
            transform: scale(0.7);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <div className={`toast-anim fixed top-6 left-1/2 z-[9999] -translate-x-1/2 px-6 py-3 rounded shadow-lg text-white font-semibold transition-all
        ${type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}
        style={{ minWidth: 220, maxWidth: 400, textAlign: 'center' }}
        onClick={onClose}
      >
        {message}
      </div>
    </>
  );
}

// Função para upload de arquivo para o MinIO
export async function uploadToMinio({
  file,
  unidade,
  tipo, // 'chamado' ou 'execucao'
  osId,
  fotoNum,
  accessKey = 'root',
  secretKey = 'Igb6029@Ikn2740@Imy4699',
  endpoint = 'https://storage.floripasoftware.com.br'
}: {
  file: File,
  unidade: string,
  tipo: 'chamado' | 'execucao',
  osId: number | string,
  fotoNum: number,
  accessKey?: string,
  secretKey?: string,
  endpoint?: string
}) {
  const bucket = `advserv-${unidade.toLowerCase()}`;
  const fileName = `${tipo}-${osId}-${fotoNum}.jpg`;
  const url = `${endpoint}/${bucket}/${fileName}`;

  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: file
  });

  if (!res.ok) {
    throw new Error('Erro ao fazer upload para o MinIO');
  }

  return url;
}

// Função para gerar URL pública
export function getMinioFileUrl({
  unidade,
  tipo,
  osId,
  fotoNum,
  endpoint = 'https://storage.floripasoftware.com.br'
}: {
  unidade: string,
  tipo: 'chamado' | 'execucao',
  osId: number | string,
  fotoNum: number,
  endpoint?: string
}) {
  const bucket = `advserv-${unidade.toLowerCase()}`;
  const fileName = `${tipo}-${osId}-${fotoNum}.jpg`;
  return `${endpoint}/${bucket}/${fileName}`;
}

// Função para deletar arquivo do MinIO
export async function deleteFromMinio({
  unidade,
  tipo,
  osId,
  fotoNum,
  endpoint = 'https://storage.floripasoftware.com.br'
}: {
  unidade: string,
  tipo: 'chamado' | 'execucao',
  osId: number | string,
  fotoNum: number,
  endpoint?: string
}) {
  const bucket = `advserv-${unidade.toLowerCase()}`;
  const fileName = `${tipo}-${osId}-${fotoNum}.jpg`;
  const url = `${endpoint}/${bucket}/${fileName}`;
  const res = await fetch(url, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error('Erro ao deletar foto do MinIO');
  }
}

export default function OSModal({ onClose, unidade, osId, onDistribuida }: { onClose: () => void, unidade: string, osId: number, onDistribuida?: () => void }) {
  const { loginGrupoId } = useAuth();
  const [loading, setLoading] = useState(true);

  // Estado local para simular upload/limpeza de fotos
  const [fotosExec, setFotosExec] = useState(fotosExecucao.map(f => ({ ...f, localUrl: f.url })));
  const [fotosFin, setFotosFin] = useState(fotosFinalizado.map(f => ({ ...f, localUrl: f.url })));
  const [urlFotoInicial, setUrlFotoInicial] = useState("");

  // Estado para departamentos
  const [departamentos, setDepartamentos] = useState<{id: number, descricao: string}[]>([]);
  // Estado para dados da OS
  const [departamentoId, setDepartamentoId] = useState<number | null>(null);
  const [descricaoLocal, setDescricaoLocal] = useState("");
  const [descricaoChamado, setDescricaoChamado] = useState("");
  const [nomeSolicitante, setNomeSolicitante] = useState("");
  const [obsSolicitante, setObsSolicitante] = useState("");
  const [obsValidadorEdit, setObsValidadorEdit] = useState("");
  const [descServico, setDescServico] = useState("");
  const [materiais, setMateriais] = useState("");
  const [equipamentos, setEquipamentos] = useState("");
  const [profissionais, setProfissionais] = useState("");

  // Estado para modal de visualização de foto
  const [fotoModalAberto, setFotoModalAberto] = useState(false);
  const [fotoModalUrl, setFotoModalUrl] = useState<string>("");
  const [zoom, setZoom] = useState(1);
  const [animarZoomIn, setAnimarZoomIn] = useState(false);
  const fotoRef = useRef<HTMLImageElement | null>(null);
  // Estado para pan/drag
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{x: number, y: number} | null>(null);
  const [imgOffset, setImgOffset] = useState<{x: number, y: number}>({x: 0, y: 0});

  // Estado para etapas dinâmicas
  const [etapas, setEtapas] = useState([
    { titulo: "Abertura", data: "", responsavel: "", tempo: "" },
    { titulo: "Distribuída", data: "", responsavel: "", tempo: "" },
    { titulo: "Aceita", data: "", responsavel: "", tempo: "" },
    { titulo: "Finalizada", data: "", responsavel: "", tempo: "" },
  ]);
  const [etapaAtual, setEtapaAtual] = useState(0);

  // Para pegar os valores brutos das datas:
  const [osAbertura, setOsAbertura] = useState<string | undefined>(undefined);
  const [osFim, setOsFim] = useState<string | undefined>(undefined);

  // No estado, guardar status e prioridade da OS
  const [osStatus, setOsStatus] = useState<string>("");
  const [osPrioridade, setOsPrioridade] = useState<string>("");
  const [osIdNum, setOsIdNum] = useState<number | null>(null);

  // Estado para executores
  type Executor = { id: number; nome: string };
  const [executores, setExecutores] = useState<Executor[]>([]);
  const [executorSelecionado, setExecutorSelecionado] = useState<string>("");
  const [prioridadeSelecionada, setPrioridadeSelecionada] = useState<string>("");

  // Estado para data/hora de visita do executor
  const [dataHoraVisita, setDataHoraVisita] = useState<Date | null>(null);

  // Estado para skeleton loader
  const [imgLoading, setImgLoading] = useState(true);

  // Toast para mensagens
  const [toast, setToast] = useState<{ open: boolean, message: string, type?: 'error' | 'success' }>({ open: false, message: '', type: 'error' });

  // Mapeamento manual de prioridade_id
  const prioridadeIdMap: Record<string, number> = {
    'AGUARDANDO': 1,
    'BAIXA': 2,
    'MEDIA': 3,
    'ALTA': 4,
    'EMERGENCIAL': 5,
  };

  // Novo: prop para callback ao aceitar
  const onAceita = onDistribuida; // Reutiliza o mesmo callback da grade

  // Estado para controlar a versão das fotos e forçar atualização
  const [fotoExecVersao, setFotoExecVersao] = useState([0, 0, 0]);
  const [fotoFinVersao, setFotoFinVersao] = useState([0, 0, 0]);

  // Estado para loading do botão gravar
  const [loadingGravar, setLoadingGravar] = useState(false);

  // Estado para loading do botão finalizar
  const [loadingFinalizar, setLoadingFinalizar] = useState(false);
  // Estado para ids do gestor e executor
  const [gestorId, setGestorId] = useState<number | null>(null);
  const [executorId, setExecutorId] = useState<number | null>(null);

  // Estado para modal de confirmação
  const [confirmarAcao, setConfirmarAcao] = useState<null | { tipo: 'cancelar' | 'finalizar' }> (null);

  // Adicionar no início do componente OSModal:
  const [modalStatusAberto, setModalStatusAberto] = useState(false);
  const [novoStatus, setNovoStatus] = useState<string>(osStatus);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Lista de status disponíveis para alteração
  const statusDisponiveis = [
    { value: 'AGUARDANDO', label: 'Aguardando' },
    { value: 'DISTRIBUIDA', label: 'Distribuída' },
    { value: 'ACEITA', label: 'Aceita' },
    { value: 'CONCLUIDA', label: 'Concluída' },
    { value: 'CANCELADA', label: 'Cancelada' },
  ];

  // Mapeamento manual de status para código numérico
  const statusCodigoMap: Record<string, number> = {
    'AGUARDANDO': 1,
    'DISTRIBUIDA': 2,
    'ACEITA': 3,
    'CONCLUIDA': 4,
    'CANCELADA': 5,
  };

  // Função para abrir modal de status
  function abrirModalStatus() {
    setNovoStatus(osStatus);
    setModalStatusAberto(true);
  }
  function fecharModalStatus() {
    setModalStatusAberto(false);
  }
  async function handleGravarStatus() {
    setLoadingStatus(true);
    try {
      // Mapeia o status textual para o código numérico
      const statusCodigo = statusCodigoMap[novoStatus];
      if (!statusCodigo) {
        setToast({ open: true, message: 'Status inválido!', type: 'error' });
        setLoadingStatus(false);
        return;
      }
      // Chama o endpoint para alterar o status
      const response = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_update_status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          unidade: (unidade || '').toLowerCase(),
          os: osIdNum ?? osId,
          status: statusCodigo
        })
      });
      const data = await response.json();
      if (Array.isArray(data) && data[0]?.success) {
        // Após sucesso, buscar novamente os dados da OS para atualizar todos os campos
        const osReadResponse = await fetch(ENDPOINTS.OS_READ, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unidade, id: osIdNum ?? osId })
        });
        const osReadData = await osReadResponse.json();
        if (osReadData && osReadData[0]) {
          const os = osReadData[0];
          setDepartamentoId(os.departamento_id);
          setDescricaoLocal(os.local_descricao || "");
          setDescricaoChamado(os.descricao_os || "");
          setNomeSolicitante(os.solicitante_nome || "");
          setObsSolicitante(os.observacoes || "");
          setObsValidadorEdit(os.obs_adm || os.obs_validador || "");
          setDescServico(os.descricao_servico_executar || os.desc_servico || "");
          setMateriais(os.materiais_necessarios || os.materiais || "");
          setEquipamentos(os.equipamentos_necessarios || os.equipamentos || "");
          setProfissionais(os.profissionais_envolvidos || os.profissionais || "");
          setUrlFotoInicial(os.url_foto || "");
          setPrioridadeSelecionada(os.prioridade_descricao?.toUpperCase().trim() || "");
          setExecutorSelecionado(os.executor_nome || "");
          setDataHoraVisita(os.dth_agenda ? new Date(os.dth_agenda.replace(' ', 'T')) : null);
          // Montar etapas dinâmicas
          const etapasMontadas = [
            {
              titulo: "Abertura",
              data: os.dth_abertura ? formatarDataHora(os.dth_abertura) : "",
              responsavel: os.solicitante_nome || "",
              tempo: "0min"
            },
            {
              titulo: "Distribuída",
              data: os.dth_distribuido ? formatarDataHora(os.dth_distribuido) : "",
              responsavel: os.gestor_nome || "",
              tempo: calcularTempo(os.dth_abertura, os.dth_distribuido)
            },
            {
              titulo: "Aceita",
              data: os.dth_inicio ? formatarDataHora(os.dth_inicio) : "",
              responsavel: os.executor_nome || "",
              tempo: calcularTempo(os.dth_distribuido, os.dth_inicio)
            },
            {
              titulo: "Finalizada",
              data: os.dth_fim ? formatarDataHora(os.dth_fim) : "",
              responsavel: (os.status_id === 4 && os.executor_nome) ? os.executor_nome : "",
              tempo: calcularTempo(os.dth_inicio, os.dth_fim)
            },
          ];
          setEtapas(etapasMontadas);
          let etapa = 0;
          if (os.status_id === 1) etapa = 0;
          else if (os.status_id === 2) etapa = 1;
          else if (os.status_id === 3) etapa = 2;
          else if (os.status_id === 4) etapa = 3;
          setEtapaAtual(etapa);
          setOsAbertura(os.dth_abertura);
          setOsFim(os.dth_fim);
          setOsStatus(os.status_descricao?.toUpperCase() || "");
          setOsPrioridade(os.prioridade_descricao?.toUpperCase() || "");
          setOsIdNum(os.id);
          setGestorId(os.gestor_id ?? null);
          setExecutorId(os.executor_id ?? null);
          // Atualizar fotos de execução e finalização com as URLs reais do MinIO
          setFotosExec([
            { id: 1, localUrl: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}` },
            { id: 2, localUrl: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}` },
            { id: 3, localUrl: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}` },
          ]);
          setFotosFin([
            { id: 1, localUrl: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}` },
            { id: 2, localUrl: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}` },
            { id: 3, localUrl: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}` },
          ]);
        }
        setToast({ open: true, message: 'Status alterado com sucesso!', type: 'success' });
        setModalStatusAberto(false);
        onClose(); // Fecha o modal principal da OS e retorna à grade
      } else {
        setToast({ open: true, message: 'Erro ao alterar status!', type: 'error' });
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao alterar status!', type: 'error' });
    } finally {
      setLoadingStatus(false);
    }
  }

  // Buscar departamentos, dados da OS e executores ao abrir o modal
  useEffect(() => {
    // Buscar departamentos e executores sempre
    fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unidade })
    })
      .then(res => res.json())
      .then(data => setDepartamentos(data));

    fetch(ENDPOINTS.USUARIO_LIST_EXECUTOR, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unidade })
    })
      .then(res => res.json())
      .then(data => setExecutores(data));

    // Só buscar dados da OS se não for Nova OS
    if (!osId) return;
    setLoading(true);
    fetch(ENDPOINTS.OS_READ, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unidade, id: osId })
    })
      .then(res => res.json())
      .then(data => {
        if (data && data[0]) {
          const os = data[0];
          setDepartamentoId(os.departamento_id);
          setDescricaoLocal(os.local_descricao || "");
          setDescricaoChamado(os.descricao_os || "");
          setNomeSolicitante(os.solicitante_nome || "");
          setObsSolicitante(os.observacoes || "");
          setObsValidadorEdit(os.obs_adm || os.obs_validador || "");
          setDescServico(os.descricao_servico_executar || os.desc_servico || "");
          setMateriais(os.materiais_necessarios || os.materiais || "");
          setEquipamentos(os.equipamentos_necessarios || os.equipamentos || "");
          setProfissionais(os.profissionais_envolvidos || os.profissionais || "");
          setUrlFotoInicial(os.url_foto || "");
          setPrioridadeSelecionada(os.prioridade_descricao?.toUpperCase().trim() || "");
          setExecutorSelecionado(os.executor_nome || "");
          setDataHoraVisita(os.dth_agenda ? new Date(os.dth_agenda.replace(' ', 'T')) : null);
          // Montar etapas dinâmicas
          const etapasMontadas = [
            {
              titulo: "Abertura",
              data: os.dth_abertura ? formatarDataHora(os.dth_abertura) : "",
              responsavel: os.solicitante_nome || "",
              tempo: "0min"
            },
            {
              titulo: "Distribuída",
              data: os.dth_distribuido ? formatarDataHora(os.dth_distribuido) : "",
              responsavel: os.gestor_nome || "",
              tempo: calcularTempo(os.dth_abertura, os.dth_distribuido)
            },
            {
              titulo: "Aceita",
              data: os.dth_inicio ? formatarDataHora(os.dth_inicio) : "",
              responsavel: os.executor_nome || "",
              tempo: calcularTempo(os.dth_distribuido, os.dth_inicio)
            },
            {
              titulo: "Finalizada",
              data: os.dth_fim ? formatarDataHora(os.dth_fim) : "",
              responsavel: (os.status_id === 4 && os.executor_nome) ? os.executor_nome : "",
              tempo: calcularTempo(os.dth_inicio, os.dth_fim)
            },
          ];
          setEtapas(etapasMontadas);
          let etapa = 0;
          if (os.status_id === 1) etapa = 0;
          else if (os.status_id === 2) etapa = 1;
          else if (os.status_id === 3) etapa = 2;
          else if (os.status_id === 4) etapa = 3;
          setEtapaAtual(etapa);
          setOsAbertura(os.dth_abertura);
          setOsFim(os.dth_fim);
          setOsStatus(os.status_descricao?.toUpperCase() || "");
          setOsPrioridade(os.prioridade_descricao?.toUpperCase() || "");
          setOsIdNum(os.id);
          setGestorId(os.gestor_id ?? null);
          setExecutorId(os.executor_id ?? null);

          // Atualizar fotos de execução e finalização com as URLs reais do MinIO
          setFotosExec([
            { id: 1, localUrl: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}` },
            { id: 2, localUrl: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}` },
            { id: 3, localUrl: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'execucao', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}` },
          ]);
          setFotosFin([
            { id: 1, localUrl: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 1 }) + `?v=${Date.now()}` },
            { id: 2, localUrl: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 2 }) + `?v=${Date.now()}` },
            { id: 3, localUrl: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}`, url: getMinioFileUrl({ unidade, tipo: 'chamado', osId: os.id, fotoNum: 3 }) + `?v=${Date.now()}` },
          ]);
        }
      })
      .finally(() => setLoading(false));
  }, [unidade, osId]);

  // Função para formatar data/hora
  function formatarDataHora(dataStr: string) {
    if (!dataStr) return "";
    const d = new Date(dataStr.replace(' ', 'T'));
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  // Função para calcular tempo entre duas datas
  function calcularTempo(inicio?: string, fim?: string) {
    if (!inicio || !fim) return "";
    const d1 = new Date(inicio.replace(' ', 'T'));
    const d2 = new Date(fim.replace(' ', 'T'));
    const diff = Math.max(0, d2.getTime() - d1.getTime());
    const min = Math.floor(diff / 60000);
    if (min < 60) return `${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) {
    return `${h}h${min % 60 > 0 ? ' ' + (min % 60) + 'min' : ''}`;
    }
    const d = Math.floor(h / 24);
    const hRest = h % 24;
    let str = `${d}d`;
    if (hRest > 0) str += ` ${hRest}h`;
    if (min % 60 > 0) str += ` ${min % 60}min`;
    return str;
  }

  // Atualizar handleFotoChange para upload imediato ao selecionar a foto
  async function handleFotoChange(idx: number, file: File, tipo: 'exec' | 'fin') {
    const url = URL.createObjectURL(file);
    if (tipo === 'exec') {
      setFotosExec(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: url } : f));
      try {
        await uploadToMinio({
          file,
          unidade,
          tipo: 'execucao',
          osId: osIdNum ?? osId,
          fotoNum: idx + 1
        });
        // Após upload, trocar para URL do MinIO com parâmetro de versão
        setTimeout(() => {
          setFotosExec(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: getFotoExecucaoUrl(idx, true) + `&v=${Date.now()}` } : f));
          setFotoExecVersao(v => v.map((val, i) => i === idx ? Date.now() : val));
        }, 1000);
        setToast({ open: true, message: 'Foto enviada com sucesso!', type: 'success' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 2000);
      } catch (err) {
        setToast({ open: true, message: 'Erro ao enviar foto para o MinIO', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } else {
      setFotosFin(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: url } : f));
      try {
        await uploadToMinio({
          file,
          unidade,
          tipo: 'chamado',
          osId: osIdNum ?? osId,
          fotoNum: idx + 1
        });
        setTimeout(() => {
          setFotosFin(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: getFotoChamadoUrl(idx, true) + `&v=${Date.now()}` } : f));
          setFotoFinVersao(v => v.map((val, i) => i === idx ? Date.now() : val));
        }, 1000);
        setToast({ open: true, message: 'Foto enviada com sucesso!', type: 'success' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 2000);
      } catch (err) {
        setToast({ open: true, message: 'Erro ao enviar foto para o MinIO', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    }
  }

  async function handleFotoLimpar(idx: number, tipo: 'exec' | 'fin') {
    try {
      if (tipo === 'exec') {
        await deleteFromMinio({
          unidade,
          tipo: 'execucao',
          osId: osIdNum ?? osId,
          fotoNum: idx + 1
        });
        setFotosExec(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: '' } : f));
        setFotoExecVersao(v => v.map((val, i) => i === idx ? Date.now() : val));
      } else {
        await deleteFromMinio({
          unidade,
          tipo: 'chamado',
          osId: osIdNum ?? osId,
          fotoNum: idx + 1
        });
        setFotosFin(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: '' } : f));
        setFotoFinVersao(v => v.map((val, i) => i === idx ? Date.now() : val));
      }
      setToast({ open: true, message: 'Foto removida com sucesso!', type: 'success' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 2000);
    } catch (err) {
      setToast({ open: true, message: 'Erro ao remover foto do MinIO', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    }
  }

  function abrirModalFoto(url: string) {
    setFotoModalUrl(url);
    setZoom(1);
    setImgOffset({x: 0, y: 0});
    setFotoModalAberto(true);
    setAnimarZoomIn(true);
    setTimeout(() => setAnimarZoomIn(false), 300); // Duração da animação
  }
  function fecharModalFoto() {
    setFotoModalAberto(false);
    setZoom(1);
    setImgOffset({x: 0, y: 0});
  }
  function handleWheelZoom(e: React.WheelEvent) {
    e.preventDefault();
    setZoom(z => {
      let novoZoom = z + (e.deltaY < 0 ? 0.15 : -0.15);
      if (novoZoom < 1) novoZoom = 1;
      if (novoZoom > 4) novoZoom = 4;
      // Resetar pan se voltar para zoom 1
      if (novoZoom === 1) setImgOffset({x: 0, y: 0});
      return novoZoom;
    });
  }

  // Funções de drag/pan
  function handleMouseDown(e: React.MouseEvent) {
    if (zoom === 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    document.body.style.cursor = 'grabbing';
  }
  function handleMouseUp() {
    setIsDragging(false);
    setDragStart(null);
    document.body.style.cursor = '';
  }
  function handleMouseMove(e: React.MouseEvent) {
    if (!isDragging || !dragStart) return;
    setImgOffset(offset => ({
      x: offset.x + (e.clientX - dragStart.x),
      y: offset.y + (e.clientY - dragStart.y)
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  }
  function handleMouseLeave() {
    setIsDragging(false);
    setDragStart(null);
    document.body.style.cursor = '';
  }

  // Calcular duração total da OS
  const duracaoTotal = etapas[0]?.data && etapas[3]?.data && etapas[0].data !== '' && etapas[3].data !== ''
    ? calcularTempo(osAbertura, osFim)
    : '';

  // Função para distribuir OS
  async function handleDistribuir() {
    if (!executorSelecionado || !prioridadeSelecionada || !obsValidadorEdit.trim()) {
      setToast({ open: true, message: 'Preencha todos os campos obrigatórios!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      return;
    }
    try {
      const prioridade_id = prioridadeIdMap[prioridadeSelecionada.trim().toUpperCase()];
      if (!prioridade_id) {
        setToast({ open: true, message: 'Prioridade inválida!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
        return;
      }
      const payload = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        dth_distribuido: new Date().toISOString().slice(0, 19).replace('T', ' '),
        status_id: 2,
        usuario_os: Number(executorSelecionado),
        obs_adm: obsValidadorEdit,
        prioridade_id: prioridade_id
      };
      const response = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_distribuir', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payload),
        cache: 'no-store'
      });
      const data = await response.json();
      if (Array.isArray(data) && data[0]?.success) {
        setToast({ open: true, message: 'OS distribuída com sucesso!', type: 'success' });
        setOsStatus('DISTRIBUIDA');
        setEtapaAtual(1);
        setTimeout(() => {
          setToast(t => ({ ...t, open: false }));
          if (onDistribuida) onDistribuida();
          onClose();
        }, 1200);
      } else {
        setToast({ open: true, message: 'Erro ao distribuir OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao distribuir OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    }
  }

  // Função para aceitar OS (status DISTRIBUIDA)
  async function handleAceitar() {
    if (!dataHoraVisita) {
      setToast({ open: true, message: 'Escolha a data e hora para agendar!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      return;
    }
    try {
      const payload = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        dth_aceita: new Date().toISOString().slice(0, 19).replace('T', ' '),
        dth_agenda: dataHoraVisita.toISOString().slice(0, 19).replace('T', ' ')
      };
      const response = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_aceitar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payload),
        cache: 'no-store'
      });
      const data = await response.json();
      if (Array.isArray(data) && data[0]?.success) {
        setToast({ open: true, message: 'OS aceita com sucesso!', type: 'success' });
        setTimeout(() => {
          setToast(t => ({ ...t, open: false }));
          if (onAceita) onAceita();
          onClose();
        }, 1200);
      } else {
        setToast({ open: true, message: 'Erro ao aceitar OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao aceitar OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    }
  }

  // Função para finalizar OS (status ACEITA) - agora aguarda gravar antes de finalizar
  async function handleFinalizarComGravar() {
    setLoadingFinalizar(true);
    try {
      // Primeiro, gravar os dados
      const payloadGravar = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        descricao_servico_executar: descServico,
        materiais_necessarios: materiais,
        equipamentos_necessarios: equipamentos,
        profissionais_envolvidos: profissionais
      };
      const responseGravar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_gravar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadGravar),
        cache: 'no-store'
      });
      const dataGravar = await responseGravar.json();
      if (!Array.isArray(dataGravar) || !dataGravar[0]?.success) {
        setToast({ open: true, message: 'Erro ao salvar dados antes de finalizar!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
        setLoadingFinalizar(false);
        return;
      }
      // Agora finalizar
      const payloadFinalizar = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        dth_fim: new Date().toISOString().slice(0, 19).replace('T', ' '),
        os_gestor: gestorId,
        usuario_os: executorId
      };
      const responseFinalizar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_finalizar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadFinalizar),
        cache: 'no-store'
      });
      const dataFinalizar = await responseFinalizar.json();
      if (Array.isArray(dataFinalizar) && dataFinalizar[0]?.success) {
        setToast({ open: true, message: 'OS finalizada com sucesso!', type: 'success' });
        if (onDistribuida) onDistribuida();
        onClose();
      } else {
        setToast({ open: true, message: 'Erro ao finalizar OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao finalizar OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    } finally {
      setLoadingFinalizar(false);
    }
  }

  async function handleCancelarComGravar() {
    setLoadingFinalizar(true);
    try {
      // Primeiro, gravar os dados
      const payloadGravar = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        descricao_servico_executar: descServico,
        materiais_necessarios: materiais,
        equipamentos_necessarios: equipamentos,
        profissionais_envolvidos: profissionais
      };
      const responseGravar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_gravar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadGravar),
        cache: 'no-store'
      });
      const dataGravar = await responseGravar.json();
      if (!Array.isArray(dataGravar) || !dataGravar[0]?.success) {
        setToast({ open: true, message: 'Erro ao salvar dados antes de cancelar!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
        setLoadingFinalizar(false);
        return;
      }
      // Agora cancelar
      const payloadCancelar = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        dth_fim: new Date().toISOString().slice(0, 19).replace('T', ' '),
        os_gestor: gestorId,
        usuario_os: executorId
      };
      console.log('Payload CANCELAR OS:', JSON.stringify(payloadCancelar, null, 2)); // Mostra o JSON formatado
      const responseCancelar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_cancelar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadCancelar),
        cache: 'no-store'
      });
      const dataCancelar = await responseCancelar.json();
      console.log('Response CANCELAR OS:', dataCancelar); // Mostra o retorno do endpoint
      if (Array.isArray(dataCancelar) && dataCancelar[0]?.success) {
        setToast({ open: true, message: 'OS cancelada com sucesso!', type: 'success' });
        if (onDistribuida) onDistribuida();
        onClose();
      } else {
        setToast({ open: true, message: 'Erro ao cancelar OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao cancelar OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    } finally {
      setLoadingFinalizar(false);
    }
  }

  // Função para finalizar OS diretamente (para gestores em AGUARDANDO)
  async function handleFinalizarAguardando() {
    setLoadingFinalizar(true);
    try {
      // Validação: gestorId é obrigatório
      if (!gestorId) {
        setToast({ open: true, message: 'Gestor responsável não identificado! Não é possível finalizar.', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
        setLoadingFinalizar(false);
        return;
      }
      const payloadFinalizar: any = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        dth_fim: new Date().toISOString().slice(0, 19).replace('T', ' '),
        os_gestor: gestorId,
        usuario_os: executorId ?? null
      };
      console.log('Payload FINALIZAR OS:', payloadFinalizar);
      const responseFinalizar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_finalizar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadFinalizar),
        cache: 'no-store'
      });
      const dataFinalizar = await responseFinalizar.json();
      if (Array.isArray(dataFinalizar) && dataFinalizar[0]?.success) {
        setToast({ open: true, message: 'OS finalizada com sucesso!', type: 'success' });
        if (onDistribuida) onDistribuida();
        onClose();
      } else {
        setToast({ open: true, message: 'Erro ao finalizar OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao finalizar OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    } finally {
      setLoadingFinalizar(false);
    }
  }

  // Função para cancelar OS diretamente (para gestores em AGUARDANDO)
  async function handleCancelarAguardando() {
    setLoadingFinalizar(true);
    try {
      // Validação: gestorId é obrigatório
      if (!gestorId) {
        setToast({ open: true, message: 'Gestor responsável não identificado! Não é possível cancelar.', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
        setLoadingFinalizar(false);
        return;
      }
      const payloadCancelar: any = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        dth_fim: new Date().toISOString().slice(0, 19).replace('T', ' '),
        os_gestor: gestorId,
        usuario_os: executorId ?? null
      };
      console.log('Payload CANCELAR OS:', JSON.stringify(payloadCancelar, null, 2));
      const responseCancelar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_cancelar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadCancelar),
        cache: 'no-store'
      });
      const dataCancelar = await responseCancelar.json();
      if (Array.isArray(dataCancelar) && dataCancelar[0]?.success) {
        setToast({ open: true, message: 'OS cancelada com sucesso!', type: 'success' });
        if (onDistribuida) onDistribuida();
        onClose();
      } else {
        setToast({ open: true, message: 'Erro ao cancelar OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao cancelar OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    } finally {
      setLoadingFinalizar(false);
    }
  }

  // Renderização condicional para status AGUARDANDO
  const isAguardando = osStatus === "AGUARDANDO";

  // Renderização condicional para status DISTRIBUÍDA
  const isDistribuida = osStatus === "DISTRIBUIDA";

  // Renderização condicional para status ACEITA
  const isAceita = osStatus === "ACEITA";

  // Renderização condicional para status CONCLUIDA
  const isConcluida = osStatus === "CONCLUIDA";

  // Renderização condicional para status CANCELADA
  const isCancelada = osStatus === "CANCELADA";

  // Para foto inicial (foto 1 do chamado):
  function getFotoInicialUrl(noCache = false) {
    if (urlFotoInicial) return urlFotoInicial;
    const url = getMinioFileUrl({
      unidade,
      tipo: 'chamado',
      osId: osIdNum ?? osId,
      fotoNum: 1
    });
    return noCache ? url + `?v=${Date.now()}` : url;
  }

  // Para fotos 2 e 3 do chamado:
  function getFotoChamadoUrl(idx: number, noCache = false) {
    const url = getMinioFileUrl({
      unidade,
      tipo: 'chamado',
      osId: osIdNum ?? osId,
      fotoNum: idx + 1
    });
    return noCache ? url + `?v=${Date.now()}` : url;
  }

  // Para fotos de execução:
  function getFotoExecucaoUrl(idx: number, noCache = false) {
    const url = getMinioFileUrl({
      unidade,
      tipo: 'execucao',
      osId: osIdNum ?? osId,
      fotoNum: idx + 1
    });
    return noCache ? url + `?v=${Date.now()}` : url;
  }

  async function handleGravar() {
    setLoadingGravar(true);
    try {
      const payloadGravar = {
        unidade: (unidade || '').toLowerCase(),
        id: osIdNum,
        descricao_servico_executar: descServico,
        materiais_necessarios: materiais,
        equipamentos_necessarios: equipamentos,
        profissionais_envolvidos: profissionais
      };
      const responseGravar = await fetch('https://automation.floripasoftware.com.br/webhook/advserv_os_gravar', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(payloadGravar),
        cache: 'no-store'
      });
      const dataGravar = await responseGravar.json();
      if (Array.isArray(dataGravar) && dataGravar[0]?.success) {
        setToast({ open: true, message: 'Alterações salvas com sucesso!', type: 'success' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 2000);
      } else {
        setToast({ open: true, message: 'Erro ao salvar alterações!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao salvar!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    } finally {
      setLoadingGravar(false);
    }
  }

  // Função para abrir modal de confirmação
  function abrirConfirmar(tipo: 'cancelar' | 'finalizar') {
    setConfirmarAcao({ tipo });
  }
  // Função para fechar modal de confirmação
  function fecharConfirmar() {
    setConfirmarAcao(null);
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Toast open={toast.open} message={toast.message} type={toast.type} onClose={() => setToast(t => ({ ...t, open: false }))} />
      {/* Modal de visualização de foto */}
      {fotoModalAberto && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-80" onClick={fecharModalFoto}>
          <div
            className="flex items-center justify-center w-full h-full"
            onClick={e => e.stopPropagation()}
            onMouseUp={e => e.stopPropagation()}
          >
            <div style={{ position: 'relative', width: 800, height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {imgLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-200 animate-pulse rounded">
                  <div className="w-32 h-32 bg-gray-300 rounded-full animate-pulse" />
                </div>
              )}
              <img
                src={fotoModalUrl}
                alt="Foto em visualização"
                width={800}
                height={600}
                className={`max-w-full max-h-full object-contain transition-transform duration-300 ${animarZoomIn ? 'scale-110' : ''}`}
                style={{ transform: `scale(${zoom}) translate(${imgOffset.x}px, ${imgOffset.y}px)`, visibility: imgLoading ? 'hidden' : 'visible' }}
                ref={fotoRef}
                onLoad={() => setImgLoading(false)}
                onWheel={handleWheelZoom}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                draggable={false}
              />
            </div>
          </div>
          <button
            className="absolute top-4 right-8 text-white text-4xl font-bold bg-black bg-opacity-40 rounded-full px-3 py-1 hover:bg-opacity-80 transition"
            onClick={fecharModalFoto}
            title="Fechar"
          >×</button>
        </div>
      )}

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-[98vw] mx-2 max-h-[90vh] overflow-y-auto relative px-2 sm:px-6">
          {/* Cabeçalho */}
          <div className="flex flex-wrap items-center justify-between border-b px-6 py-4 bg-gray-100 relative">
            <span className="font-bold text-lg text-black">OS #{osIdNum ?? ""}</span>
            <div className="flex items-center gap-2 ml-auto pr-14">
              {osPrioridade && (
                <span className={`flex items-center gap-2 px-3 py-1 rounded font-semibold text-sm shadow ${prioridades[osPrioridade as keyof typeof prioridades] || "bg-gray-200 text-gray-900"}`}>
                  <i className={`fa ${prioridadeIcones[osPrioridade as keyof typeof prioridadeIcones] || "fa-hourglass-end"}`}></i>
                  Prioridade: {osPrioridade}
                </span>
              )}
              {osStatus && (
                <span
                  className={`flex items-center gap-2 px-3 py-1 rounded font-semibold text-sm shadow ${statusCores[osStatus as keyof typeof statusCores] || "bg-gray-200 text-gray-900"}`}
                  style={(loginGrupoId === "1" || loginGrupoId === "2") ? { cursor: 'pointer' } : {}}
                  title={(loginGrupoId === "1" || loginGrupoId === "2") ? 'Clique para alterar o status' : undefined}
                  onClick={() => {
                    if (loginGrupoId === "1" || loginGrupoId === "2") abrirModalStatus();
                  }}
                >
                  <i className={`fa ${statusIcones[osStatus as keyof typeof statusIcones] || "fa-hourglass-end"}`}></i>
                  Status: {osStatus.charAt(0) + osStatus.slice(1).toLowerCase()}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="absolute top-2 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow hover:bg-red-100 transition-colors border border-gray-200"
              aria-label="Fechar"
              title="Fechar"
            >
              <span className="text-2xl text-gray-700 hover:text-red-600 font-bold pointer-events-none">×</span>
            </button>
          </div>

          {/* Etapas */}
          <div className="flex flex-col md:flex-row gap-4 px-6 py-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 justify-center">
                <span className="font-bold text-base text-gray-700 text-center w-full">
                  Andamento do Serviço
                  {duracaoTotal && (
                    <span className="font-normal text-xs text-gray-500 ml-2">( Duração Total da OS : {duracaoTotal} )</span>
                  )}
                </span>
              </div>
              <div className="relative flex flex-row items-center justify-between min-h-[80px] min-w-0 px-2">
                {/* Linha de tempo: parte concluída em cinza escuro */}
                <div className="absolute left-0 top-[38%] h-1 z-0" style={{
                  width: `${etapas.length === 1 ? 100 : (etapaAtual) / (etapas.length - 1) * 100}%`,
                  background: '#444950',
                  transform: 'translateY(-50%)',
                  transition: 'width 0.3s',
                }} />
                {/* Linha de tempo: parte cinza claro do estágio atual até o final */}
                <div className="absolute right-0 top-[38%] h-px bg-[#c0c0c0] z-0" style={{
                  left: `${etapas.length === 1 ? 100 : (etapaAtual) / (etapas.length - 1) * 100}%`,
                  transform: 'translateY(-50%)',
                  transition: 'left 0.3s',
                }} />
                {etapas.map((etapa, idx) => {
                  let cor = 'bg-gray-400';
                  if (idx < etapaAtual) cor = 'bg-blue-600';
                  else if (idx === etapaAtual) cor = 'bg-green-600';
                  const mostrarInfo = etapaAtual >= idx;
                  return (
                    <div key={idx} className="flex flex-col items-center z-10 min-w-[80px]">
                      <div className={`rounded-full w-10 h-10 flex items-center justify-center font-bold text-white border-4 border-white shadow z-10 ${cor}`}>{idx + 1}</div>
                      <span className="text-xs font-semibold mt-1 whitespace-nowrap text-black">{etapa.titulo}</span>
                      <span className="text-xs text-black whitespace-nowrap">{mostrarInfo ? etapa.data : ''}</span>
                      <span className="text-xs text-black whitespace-nowrap">{mostrarInfo ? etapa.responsavel : ''}</span>
                      <span className="text-xs text-black whitespace-nowrap">{mostrarInfo ? etapa.tempo : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Dados do Serviço a ser executado */}
          <div className="px-6 py-4">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-t font-bold">DADOS referente ao SERVIÇO a ser executado</div>
            <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
              <div>
                <label className="block text-xs font-semibold text-gray-700">Departamento</label>
                <input
                  className="w-full bg-gray-200 border-none p-0 m-0 text-base text-black focus:outline-none focus:ring-0"
                  value={departamentos.find(d => d.id === departamentoId)?.descricao || ''}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Descrição LOCAL</label>
                <input type="text" value={descricaoLocal} readOnly className="w-full bg-gray-200 border-none p-0 m-0 text-base text-black focus:outline-none focus:ring-0" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700">Descrição do Chamado</label>
                <textarea value={descricaoChamado} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={4} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Nome Solicitante</label>
                <input type="text" value={nomeSolicitante} readOnly className="w-full bg-gray-200 border-none p-0 m-0 text-base text-black focus:outline-none focus:ring-0" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Observação do Validador</label>
                <textarea value={obsSolicitante} readOnly className="w-full bg-gray-200 border-none p-0 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={1} />
              </div>
              {isAguardando && (
                <>
                  {/* Primeira linha: Observação Validador + Executor */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700">Observação DO VALIDADOR</label>
                      <textarea 
                        value={obsValidadorEdit} 
                        onChange={e => setObsValidadorEdit(e.target.value)} 
                        className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" 
                        rows={1} 
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700">Executor</label>
                      <select
                        className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                        value={executorSelecionado}
                        onChange={e => setExecutorSelecionado(e.target.value)}
                      >
                        <option value="">Selecione o executor</option>
                        {executores.map(exec => (
                          <option key={exec.id} value={exec.id}>{exec.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Segunda linha: Prioridade + Botão DISTRIBUIR */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700">Prioridade</label>
                      <select
                        className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                        value={prioridadeSelecionada}
                        onChange={e => setPrioridadeSelecionada(e.target.value)}
                      >
                        <option value="">Selecione a prioridade</option>
                        {Object.keys(prioridades).map(prio => (
                          <option key={prio} value={prio}>{prio.charAt(0) + prio.slice(1).toLowerCase()}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700 invisible">Prioridade</label>
                      <button
                        className="w-full bg-yellow-400 text-gray-900 hover:bg-yellow-500 border border-yellow-500 transition p-2 font-bold rounded cursor-pointer"
                        disabled={
                          !prioridadeSelecionada ||
                          !executorSelecionado ||
                          !obsValidadorEdit.trim()
                        }
                        onClick={handleDistribuir}
                      >
                        DISTRIBUIR
                      </button>
                    </div>
                  </div>
                </>
              )}
              {isDistribuida && (
                <>
                  {/* Primeira linha: Observação Validador + Executor (readonly) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700">Observação DO VALIDADOR</label>
                      <textarea 
                        value={obsValidadorEdit} 
                        readOnly
                        className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" 
                        rows={1} 
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700">Executor</label>
                      <input
                        type="text"
                        value={executores.find(exec => exec.id === Number(executorSelecionado))?.nome || ''}
                        readOnly
                        className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>
                  {/* Segunda linha: Prioridade (readonly) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="block text-xs font-semibold text-gray-700">Prioridade</label>
                      <input
                        type="text"
                        value={prioridadeSelecionada ? prioridadeSelecionada.charAt(0) + prioridadeSelecionada.slice(1).toLowerCase() : ''}
                        readOnly
                        className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Fotos do serviço a ser executado */}
          <div className="px-6 pb-4">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-t font-bold">FOTOS referente ao SERVIÇO a ser executado</div>
            <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50">
              {/* Foto inicial */}
              <div className="flex flex-col items-center">
                <div className="w-full h-72 bg-gray-200 rounded flex items-center justify-center overflow-hidden relative">
                  {getFotoInicialUrl(true) ? (
                    <img
                      src={getFotoInicialUrl(true)}
                      alt="Foto inicial"
                      width={200}
                      height={150}
                      className="object-contain h-full cursor-pointer"
                      onClick={() => abrirModalFoto(getFotoInicialUrl(true))}
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                      onError={e => (e.currentTarget.src = imagemPadrao)}
                    />
                  ) : (
                    <img
                      src={imagemPadrao}
                      alt="Sem foto inicial"
                      width={80}
                      height={80}
                      className="object-contain opacity-60"
                      style={{ maxWidth: '80px', maxHeight: '80px' }}
                    />
                  )}
                </div>
                <span className="mt-2 text-xs font-semibold">FOTO INICIAL (Smartphone)</span>
              </div>
              {/* Foto 2 */}
              <div className="flex flex-col items-center">
                <div className="w-full h-72 bg-gray-200 rounded flex items-center justify-center overflow-hidden relative">
                  <img
                    src={fotosFin[1]?.localUrl || imagemPadrao}
                    alt="Foto 2"
                    width={200}
                    height={150}
                    className="object-contain h-full cursor-pointer"
                    onClick={() => abrirModalFoto(fotosFin[1]?.localUrl || imagemPadrao)}
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    onError={e => (e.currentTarget.src = imagemPadrao)}
                  />
                  {/* Botões sobrepostos, habilitados se status ACEITA */}
                  {isAceita && (
                    <div className="absolute top-2 right-2 flex flex-col gap-2 z-20">
                      <label className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow cursor-pointer flex items-center justify-center" title="Enviar foto">
                        <i className="fa fa-camera"></i>
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files && e.target.files[0] && handleFotoChange(1, e.target.files[0], 'fin')} />
                      </label>
                      <button className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow flex items-center justify-center cursor-pointer" title="Limpar foto" onClick={() => handleFotoLimpar(1, 'fin')}>
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  )}
                </div>
                <span className="mt-2 text-xs font-semibold">FOTO[2]</span>
              </div>
              {/* Foto 3 */}
              <div className="flex flex-col items-center">
                <div className="w-full h-72 bg-gray-200 rounded flex items-center justify-center overflow-hidden relative">
                  <img
                    src={fotosFin[2]?.localUrl || imagemPadrao}
                    alt="Foto 3"
                    width={200}
                    height={150}
                    className="object-contain h-full cursor-pointer"
                    onClick={() => abrirModalFoto(fotosFin[2]?.localUrl || imagemPadrao)}
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    onError={e => (e.currentTarget.src = imagemPadrao)}
                  />
                  {/* Botões sobrepostos, habilitados se status ACEITA */}
                  {isAceita && (
                    <div className="absolute top-2 right-2 flex flex-col gap-2 z-20">
                      <label className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow cursor-pointer flex items-center justify-center" title="Enviar foto">
                        <i className="fa fa-camera"></i>
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files && e.target.files[0] && handleFotoChange(2, e.target.files[0], 'fin')} />
                      </label>
                      <button className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow flex items-center justify-center cursor-pointer" title="Limpar foto" onClick={() => handleFotoLimpar(2, 'fin')}>
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  )}
                </div>
                <span className="mt-2 text-xs font-semibold">FOTO[3]</span>
              </div>
            </div>
          </div>

          {/* Campo de data/hora para o executor escolher quando irá ao local */}
          {isDistribuida && (
            <div className="px-6 pb-4">
              <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-2 rounded">
                <span className="font-bold">Escolha a data e hora que pretende ir ao local:</span>
                <div className="flex flex-col sm:flex-row gap-2 mt-2 items-center">
                  <DatePicker
                    selected={dataHoraVisita}
                    onChange={date => setDataHoraVisita(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    timeCaption="Hora"
                    className="border rounded p-2"
                    disabled={!isDistribuida}
                    placeholderText="Clique para escolher"
                  />
                  <button
                    className={`px-6 py-2 rounded font-bold cursor-pointer transition-colors 
                      ${dataHoraVisita ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    disabled={!dataHoraVisita}
                    onClick={handleAceitar}
                  >
                    ACEITAR
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bloco de dados referente ao serviço em execução */}
          {isAceita && (
            <div className="px-6 py-4">
              <div className="bg-green-600 text-white px-4 py-2 rounded-t font-bold">DADOS referente ao SERVIÇO em EXECUÇÃO</div>
              <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50">
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-gray-700">Descrição do serviço já realizado</label>
                  <textarea 
                    value={descServico} 
                    onChange={e => setDescServico(e.target.value)} 
                    className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" 
                    rows={4} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Materiais necessários para execução</label>
                  <textarea 
                    value={materiais} 
                    onChange={e => setMateriais(e.target.value)} 
                    className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" 
                    rows={3} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Equipamentos necessários para execução</label>
                  <textarea 
                    value={equipamentos} 
                    onChange={e => setEquipamentos(e.target.value)} 
                    className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" 
                    rows={3} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Profissionais envolvidos na execução</label>
                  <textarea 
                    value={profissionais} 
                    onChange={e => setProfissionais(e.target.value)} 
                    className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" 
                    rows={3} 
                  />
                </div>
              </div>
            </div>
          )}

          {/* Bloco de fotos referente ao serviço quando finalizado */}
          {isAceita && (
            <div className="px-6 pb-4">
              <div className="bg-green-600 text-white px-4 py-2 rounded-t font-bold">FOTOS referente ao SERVIÇO em EXECUÇÃO</div>
              <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50">
                {fotosExec.map((foto, idx) => (
                  <div key={foto.id} className="flex flex-col items-center">
                    <div className="w-full h-72 bg-gray-200 rounded flex items-center justify-center overflow-hidden relative">
                      <img
                        src={foto.localUrl || imagemPadrao}
                        alt={`Foto execução ${idx+1}`}
                        width={200}
                        height={150}
                        className="object-contain h-full cursor-pointer"
                        onClick={() => abrirModalFoto(foto.localUrl || imagemPadrao)}
                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                        onError={e => (e.currentTarget.src = imagemPadrao)}
                      />
                      {/* Botões sobrepostos */}
                      <div className="absolute top-2 right-2 flex flex-col gap-2 z-20">
                        <label className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow cursor-pointer flex items-center justify-center" title="Enviar foto">
                          <i className="fa fa-camera"></i>
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files && e.target.files[0] && handleFotoChange(idx, e.target.files[0], 'exec')} />
                        </label>
                        <button className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow flex items-center justify-center cursor-pointer" title="Limpar foto" onClick={() => handleFotoLimpar(idx, 'exec')}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <span className="mt-2 text-xs font-semibold">FOTO[{foto.id}]</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Após os blocos de fotos e dados, exibir todos os blocos em modo readonly se isConcluida */}
          {(isConcluida || isCancelada) && (
            <>
              {/* Bloco de dados referente ao serviço em execução (readonly) */}
              <div className="px-6 py-4">
                <div className="bg-green-600 text-white px-4 py-2 rounded-t font-bold">DADOS referente ao SERVIÇO em EXECUÇÃO</div>
                <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50">
                  <div className="md:col-span-3">
                    <label className="block text-xs font-semibold text-gray-700">Descrição do serviço já realizado</label>
                    <textarea value={descServico} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={4} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Materiais necessários para execução</label>
                    <textarea value={materiais} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={3} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Equipamentos necessários para execução</label>
                    <textarea value={equipamentos} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={3} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Profissionais envolvidos na execução</label>
                    <textarea value={profissionais} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={3} />
                  </div>
                  {/* Campos adicionais importantes */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Observação do Validador</label>
                    <textarea value={obsValidadorEdit} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none" rows={1} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Executor Escolhido</label>
                    <input type="text" value={executores.find(exec => exec.id === Number(executorSelecionado))?.nome || ''} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Prioridade</label>
                    <input type="text" value={prioridadeSelecionada ? prioridadeSelecionada.charAt(0) + prioridadeSelecionada.slice(1).toLowerCase() : ''} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700">Data e Hora escolhida para ir ao local</label>
                    <input type="text" value={dataHoraVisita ? new Date(dataHoraVisita).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''} readOnly className="w-full bg-gray-200 border-none p-2 m-0 text-base text-black focus:outline-none focus:ring-0" />
                  </div>
                </div>
              </div>
              {/* Bloco de fotos referente ao serviço quando finalizado (readonly) */}
              <div className="px-6 pb-4">
                <div className="bg-green-600 text-white px-4 py-2 rounded-t font-bold">FOTOS referente ao SERVIÇO quando FINALIZADO</div>
                <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50">
                  {fotosFin.map((foto, idx) => (
                    <div key={foto.id} className="flex flex-col items-center">
                      <div className="w-full h-72 bg-gray-200 rounded flex items-center justify-center overflow-hidden relative">
                        <img
                          src={foto.localUrl || imagemPadrao}
                          alt={idx === 0 ? 'Foto inicial' : ''}
                          width={200}
                          height={150}
                          className="object-contain h-full cursor-pointer"
                          onClick={() => abrirModalFoto(foto.localUrl || imagemPadrao)}
                          style={{ maxWidth: '100%', maxHeight: '100%' }}
                          onError={e => (e.currentTarget.src = imagemPadrao)}
                        />
                      </div>
                      <span className="mt-2 text-xs font-semibold">FOTO[{foto.id}]</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Rodapé com botões */}
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
            {isAceita && (
              <div className="flex justify-between items-center w-full">
                <div className="flex gap-4">
                  <button
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-bold cursor-pointer"
                    onClick={() => abrirConfirmar('cancelar')}
                    disabled={loadingFinalizar}
                  >
                    {loadingFinalizar ? 'Cancelando...' : 'CANCELAR'}
                  </button>
                  <button
                    className={`bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded font-bold cursor-pointer ${loadingGravar ? 'opacity-60 cursor-not-allowed' : ''}`}
                    onClick={handleGravar}
                    disabled={loadingGravar || loadingFinalizar}
                  >
                    {loadingGravar ? 'Gravando...' : 'GRAVAR'}
                  </button>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold cursor-pointer"
                    onClick={() => abrirConfirmar('finalizar')}
                    disabled={loadingFinalizar}
                  >
                    {loadingFinalizar ? 'Finalizando...' : 'FINALIZAR'}
                  </button>
                </div>
                <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-bold">FECHAR</button>
              </div>
            )}
            {isAguardando && loginGrupoId === "2" && (
              <div className="flex flex-col gap-2 w-full items-stretch">
                <button
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold cursor-pointer mb-2"
                  onClick={() => abrirConfirmar('finalizar')}
                  disabled={loadingFinalizar}
                >
                  {loadingFinalizar ? 'Finalizando...' : 'FINALIZAR'}
                </button>
                <button
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-bold cursor-pointer"
                  onClick={() => abrirConfirmar('cancelar')}
                  disabled={loadingFinalizar}
                >
                  {loadingFinalizar ? 'Cancelando...' : 'CANCELAR'}
                </button>
              </div>
            )}
            {isAguardando && loginGrupoId !== "2" && (
              <div className="flex justify-end w-full">
                <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-bold">FECHAR</button>
              </div>
            )}
            {(isConcluida || isCancelada) && (
              <div className="flex justify-end w-full">
                <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-bold">FECHAR</button>
              </div>
            )}
            {isDistribuida && (
              <div className="flex justify-end w-full">
                <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-bold">FECHAR</button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Modal de confirmação de ação (cancelar/finalizar) */}
      {confirmarAcao && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <style>{`
            .modal-anim {
              animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            }
            @keyframes zoomInFade {
              0% {
                opacity: 0;
                transform: scale(0.7);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
          <div className="modal-anim bg-white rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
            <div className="flex flex-col items-center mb-4">
              {confirmarAcao.tipo === 'cancelar' ? (
                <span className="text-red-500 text-5xl mb-2"><FaTimesCircle /></span>
              ) : (
                <span className="text-green-500 text-5xl mb-2"><FaCheckCircle /></span>
              )}
              <span className={`text-xl font-bold mb-2 ${confirmarAcao.tipo === 'cancelar' ? 'text-red-600' : 'text-green-600'}`}>Confirma {confirmarAcao.tipo === 'cancelar' ? 'CANCELAR' : 'FINALIZAR'}?</span>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              <button
                className={`px-6 py-2 rounded font-bold ${confirmarAcao.tipo === 'cancelar' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                onClick={async () => {
                  fecharConfirmar();
                  if (confirmarAcao.tipo === 'cancelar') await handleCancelarComGravar();
                  else await handleFinalizarComGravar();
                }}
                disabled={loadingFinalizar}
              >
                {confirmarAcao.tipo === 'cancelar' ? 'Cancelar OS' : 'Finalizar OS'}
              </button>
              <button
                className="bg-gray-400 text-white px-6 py-2 rounded font-bold hover:bg-gray-500"
                onClick={fecharConfirmar}
                disabled={loadingFinalizar}
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}
      {modalStatusAberto && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <div className="modal-anim bg-white rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
            <div className="flex flex-col items-center mb-4">
              <span className="text-blue-500 text-5xl mb-2"><i className="fa fa-exchange-alt"></i></span>
              <span className="text-xl font-bold mb-2 text-blue-600">Alterar Status da OS</span>
            </div>
            <div className="mb-4">
              <select
                className="w-full border border-gray-300 rounded p-2 text-base"
                style={{ color: '#000', background: '#fff' }}
                value={novoStatus}
                onChange={e => setNovoStatus(e.target.value)}
              >
                {statusDisponiveis.map(st => (
                  <option key={st.value} value={st.value} style={{ color: '#000', background: '#fff' }}>
                    {st.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              <button
                className="px-6 py-2 rounded font-bold bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleGravarStatus}
                disabled={loadingStatus}
              >
                {loadingStatus ? 'Gravando...' : 'Gravar'}
              </button>
              <button
                className="bg-gray-400 text-white px-6 py-2 rounded font-bold hover:bg-gray-500"
                onClick={fecharModalStatus}
                disabled={loadingStatus}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 